import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import AccountTreeRoundedIcon
  from '@mui/icons-material/AccountTreeRounded';
import ExpandLessRoundedIcon
  from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon
  from '@mui/icons-material/ExpandMoreRounded';
import InsightsRoundedIcon
  from '@mui/icons-material/InsightsRounded';
import OpenInNewRoundedIcon
  from '@mui/icons-material/OpenInNewRounded';

import {
  getMarginCenterCustomerEconomicStudy,
} from '../services/marginCenterService';

import type {
  MarginCenterComparisonMode,
} from '../types/marginCenter';

import type {
  MarginCenterCustomerEconomicStudyFinding,
  MarginCenterCustomerEconomicStudyInvestigationPattern,
  MarginCenterCustomerEconomicStudyInvestigationPatternCode,
  MarginCenterCustomerEconomicStudyParams,
} from '../types/customerEconomicStudy';

import {
  formatMarginCenterCurrency as formatMoney,
  formatMarginCenterNumber as formatNumber,
} from '../utils/numberFormat';


interface CustomerEconomicStudyPanelProps {
  customerId: string;
  startDate?: string | null;
  endDate?: string | null;
  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate?: string | null;
  comparisonEndDate?: string | null;
  currencyCode: string | null;
}

function formatPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${formatNumber(value, 2)} %`;
}


function formatSignedMoney(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const formatted = formatMoney(
    Math.abs(value),
    currencyCode,
  );

  if (value > 0) {
    return `+${formatted}`;
  }

  if (value < 0) {
    return `−${formatted}`;
  }

  return formatted;
}


function readNumber(
  value: string | number | null | undefined,
): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}


function readString(
  value: string | number | null | undefined,
): string | null {
  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  return null;
}


function movementColor(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined || value === 0) {
    return '#e2e8f0';
  }

  return value > 0
    ? '#34d399'
    : '#fb7185';
}


function effectLabel(
  value: string | null,
): string {
  if (value === 'quantity') return 'cantidad';
  if (value === 'price') return 'precio';
  if (value === 'cost') return 'coste';

  return 'factor contable';
}


function limitationText(
  code: string,
): string {
  if (code === 'comparison_required') {
    return 'Se necesita un periodo comparado para estudiar el cambio.';
  }

  if (code === 'comparison_required_for_change_analysis') {
    return 'La atribución del cambio requiere comparación temporal.';
  }

  if (code === 'unresolved_cost_rows_excluded_from_margin_attribution') {
    return 'Las filas con coste no resuelto quedan fuera de la atribución de margen.';
  }

  if (code === 'continued_relationships_not_eligible_for_decomposition') {
    return 'Algunas relaciones continuas no cumplen las condiciones para descomponer precio, cantidad y coste.';
  }

  if (code === 'current_period_extends_outside_dataset') {
    return 'El periodo actual incluye fechas fuera del histórico disponible.';
  }

  if (code === 'comparison_period_extends_outside_dataset') {
    return 'El periodo comparado incluye fechas fuera del histórico disponible.';
  }

  if (code === 'reconciliation_outside_numeric_tolerance') {
    return 'La reconciliación supera la tolerancia numérica contractual.';
  }

  return code.split('_').join(' ');
}


function findingCopy(
  finding: MarginCenterCustomerEconomicStudyFinding,
  currencyCode: string | null,
): {
  title: string;
  body: string;
  productId: string | null;
  toneValue: number | null;
} | null {
  if (finding.code === 'margin_balance') {
    const marginDelta = readNumber(
      finding.values.margin_documented_delta,
    );

    const revenueDelta = readNumber(
      finding.values.revenue_delta,
    );

    return {
      title:
        `Margen ${formatSignedMoney(
          marginDelta,
          currencyCode,
        )}`,
      body:
        `Ingresos ${formatSignedMoney(
          revenueDelta,
          currencyCode,
        )} frente al periodo comparado.`,
      productId: null,
      toneValue: marginDelta,
    };
  }

  if (finding.code === 'margin_change_concentration') {
    const label =
      readString(finding.values.product_label)
      ?? finding.entity_keys.product_id
      ?? 'Producto';

    const marginDelta = readNumber(
      finding.values.margin_delta_documented,
    );

    const share = readNumber(
      finding.values
        .share_of_absolute_documented_movement_pct,
    );

    return {
      title:
        `${label}: ${formatSignedMoney(
          marginDelta,
          currencyCode,
        )}`,
      body:
        `Representa ${formatPercentage(
          share,
        )} de todos los cambios de margen entre productos.`,
      productId:
        finding.entity_keys.product_id ?? null,
      toneValue: marginDelta,
    };
  }

  if (finding.code === 'eligible_decomposition_balance') {
    const eligibleCount = readNumber(
      finding.values.eligible_relationship_count,
    );

    const dominantEffect =
      readString(finding.values.dominant_effect);

    const dominantAmount = readNumber(
      finding.values.dominant_effect_amount,
    );

    const dominantShare = readNumber(
      finding.values
        .dominant_effect_share_of_absolute_effects_pct,
    );

    return {
      title:
        `${effectLabel(
          dominantEffect,
        ) === 'cantidad'
          ? 'Cantidad'
          : effectLabel(dominantEffect) === 'precio'
            ? 'Precio'
            : effectLabel(dominantEffect) === 'coste'
              ? 'Coste'
              : 'Principal componente'
        }: ${formatSignedMoney(
          dominantAmount,
          currencyCode,
        )}`,
      body:
        `Es el componente con más peso en ${formatNumber(
          eligibleCount,
          0,
        )} productos comparables (${formatPercentage(
          dominantShare,
        )} del total de cantidad, precio y coste).`,
      productId: null,
      toneValue: dominantAmount,
    };
  }

  return null;
}

function investigationPatternCopy(
  pattern:
    MarginCenterCustomerEconomicStudyInvestigationPattern,
  currencyCode: string | null,
): {
  title: string;
  tag: string;
  body: string;
  toneValue: number | null;
} {
  const count = pattern.relationship_count;
  const metrics = pattern.metrics;

  if (pattern.code === 'current_only_products') {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} productos aparecen en el periodo actual`,
      tag: 'Entrada de cartera',
      body:
        `En conjunto aportan ${formatSignedMoney(
          metrics.margin_delta_documented,
          currencyCode,
        )} de margen y ${formatSignedMoney(
          metrics.revenue_delta,
          currencyCode,
        )} de ingresos.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  if (
    pattern.code === 'comparison_only_products'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} productos han dejado de tener actividad`,
      tag: 'Salida de cartera',
      body:
        `Frente al periodo comparado representan ${formatSignedMoney(
          metrics.margin_delta_documented,
          currencyCode,
        )} de margen y ${formatSignedMoney(
          metrics.revenue_delta,
          currencyCode,
        )} de ingresos.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  if (
    pattern.code === 'revenue_up_margin_down'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} relaciones venden más pero dejan menos margen`,
      tag: 'Rentabilidad',
      body:
        `Los ingresos cambian ${formatSignedMoney(
          metrics.revenue_delta,
          currencyCode,
        )}, mientras el margen cambia ${formatSignedMoney(
          metrics.margin_delta_documented,
          currencyCode,
        )}.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  if (
    pattern.code === 'revenue_down_margin_up'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} ${
          count === 1
            ? 'relación vende'
            : 'relaciones venden'
        } menos pero ${
          count === 1
            ? 'deja'
            : 'dejan'
        } más margen`,
      tag: 'Rentabilidad',
      body:
        `Los ingresos cambian ${formatSignedMoney(
          metrics.revenue_delta,
          currencyCode,
        )}, mientras el margen cambia ${formatSignedMoney(
          metrics.margin_delta_documented,
          currencyCode,
        )}.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  if (
    pattern.code === 'price_up_quantity_down'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} relaciones con precio al alza y cantidad a la baja`,
      tag: 'Precio / volumen',
      body:
        `El efecto precio suma ${formatSignedMoney(
          metrics.price_effect,
          currencyCode,
        )} y el efecto cantidad ${formatSignedMoney(
          metrics.quantity_effect,
          currencyCode,
        )}. Coinciden ambos movimientos; no implica que uno cause el otro.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  return {
    title:
      `${formatNumber(
        count,
        0,
      )} relaciones con coste unitario al alza y margen a la baja`,
    tag: 'Coste',
    body:
      `En conjunto, los ingresos cambian ${formatSignedMoney(
        metrics.revenue_delta,
        currencyCode,
      )} y el margen ${formatSignedMoney(
        metrics.margin_delta_documented,
        currencyCode,
      )}. Coinciden ambos movimientos; no implica causalidad.`,
    toneValue:
      metrics.margin_delta_documented,
  };
}

function MetricBlock({
  label,
  value,
  detail,
  valueColor = '#f8fafc',
}: {
  label: string;
  value: string;
  detail?: string;
  valueColor?: string;
}) {
  return (
    <Box
      sx={{
        minWidth: 0,
        p: 1.5,
        borderRadius: 2.5,
        border: '1px solid rgba(148,163,184,0.14)',
        bgcolor: 'rgba(15,23,42,0.42)',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: '#94a3b8',
          fontWeight: 750,
          letterSpacing: '0.02em',
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.45,
          color: valueColor,
          fontWeight: 850,
          fontSize: {
            xs: '1.15rem',
            md: '1.3rem',
          },
          lineHeight: 1.2,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>

      {detail && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.55,
            color: '#64748b',
            lineHeight: 1.45,
          }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  );
}


export default function CustomerEconomicStudyPanel({
  customerId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  currencyCode,
}: CustomerEconomicStudyPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [
    openPatternCode,
    setOpenPatternCode,
  ] = useState<
    MarginCenterCustomerEconomicStudyInvestigationPatternCode
    | null
  >(null);

  const params = useMemo<MarginCenterCustomerEconomicStudyParams>(
    () => ({
      start_date: startDate ?? undefined,
      end_date: endDate ?? undefined,
      comparison_mode: comparisonMode,
      comparison_start_date:
        comparisonMode === 'custom'
          ? comparisonStartDate ?? undefined
          : undefined,
      comparison_end_date:
        comparisonMode === 'custom'
          ? comparisonEndDate ?? undefined
          : undefined,
      sort_field: 'absolute_margin_delta',
      sort_direction: 'desc',
      limit: 25,
    }),
    [
      comparisonEndDate,
      comparisonMode,
      comparisonStartDate,
      endDate,
      startDate,
    ],
  );

  const studyQuery = useQuery({
    queryKey: [
      'margin-center',
      'customer-economic-study',
      customerId,
      params,
    ],
    queryFn: () =>
      getMarginCenterCustomerEconomicStudy(
        customerId,
        params,
      ),
    enabled: Boolean(customerId),
    placeholderData: (previousData) => previousData,
    retry: false,
  });

  const data = studyQuery.data;

  const openRelationship = (
    productId: string,
  ) => {
    const next = new URLSearchParams(searchParams);

    next.delete('relationshipCursor');
    next.set('relationshipProduct', productId);

    setSearchParams(next);
  };

  if (studyQuery.isLoading && !data) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.54)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={1.25}>
            <Typography
              variant="h6"
              sx={{
                color: '#f8fafc',
                fontWeight: 850,
              }}
            >
              Estudio económico
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: '#94a3b8' }}
            >
              Analizando qué explica el cambio económico de este cliente…
            </Typography>

            <LinearProgress />
          </Stack>
        </Box>
      </Paper>
    );
  }

  if (studyQuery.isError) {
    return (
      <Alert severity="error">
        No se ha podido calcular el estudio económico del cliente.
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  if (data.status === 'empty_dataset') {
    return (
      <Alert severity="info">
        No existe actividad transaccional para construir el estudio económico.
      </Alert>
    );
  }

  if (
    data.status === 'customer_not_found'
    || !data.customer
    || !data.financial
  ) {
    return (
      <Alert severity="warning">
        No se ha encontrado evidencia suficiente para construir el estudio económico de este cliente.
      </Alert>
    );
  }

  const effectiveCurrencyCode =
    data.presentation.currency_code
    ?? currencyCode;

  const comparisonAvailable =
    data.selection.comparison_mode !== 'none'
    && data.financial.comparison !== null
    && data.financial.changes !== null;

  const coverage = data.analysis_coverage;
  const reconciliation = data.reconciliation;
  const contributions = data.contributions;

  const findingCopies = data.findings
    .filter(
      (finding) =>
        finding.code
        === 'eligible_decomposition_balance',
    )
    .map((finding) =>
      findingCopy(
        finding,
        effectiveCurrencyCode,
      )
    )
    .filter(
      (
        finding,
      ): finding is NonNullable<
        ReturnType<typeof findingCopy>
      > => finding !== null,
    )
    .slice(0, 1);

  const topRelationships =
    contributions?.top_absolute ?? [];

  const decomposition =
    contributions?.decomposition ?? null;

  const investigationPatterns =
    comparisonAvailable
      ? data.investigation_patterns.slice(0, 4)
      : [];

  const statusLabel =
    reconciliation?.reconciled === true
      ? 'Cálculo comprobado'
      : data.status === 'partial'
        ? 'Datos parciales'
        : data.status === 'insufficient_evidence'
          ? 'Faltan datos'
          : 'Análisis disponible';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(56,189,248,0.22)',
        bgcolor: 'rgba(15,23,42,0.62)',
        overflow: 'hidden',
      }}
    >
      {studyQuery.isFetching && (
        <LinearProgress />
      )}

      <Box sx={{ p: { xs: 2, md: 2.75 } }}>
        <Stack spacing={2.25}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={1.5}
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
            justifyContent="space-between"
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <InsightsRoundedIcon
                  sx={{
                    color: '#38bdf8',
                    fontSize: 22,
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  Estudio económico
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.55,
                  color: '#94a3b8',
                  maxWidth: 860,
                  lineHeight: 1.55,
                }}
              >
                Qué cambios económicos merece revisar y en qué productos conviene profundizar.
              </Typography>
            </Box>

            <Chip
              size="small"
              label={statusLabel}
              sx={{
                bgcolor: 'rgba(56,189,248,0.10)',
                color: '#bae6fd',
                border:
                  '1px solid rgba(56,189,248,0.18)',
                fontWeight: 800,
              }}
            />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                xl: 'minmax(300px, 0.34fr) minmax(0, 0.66fr)',
              },
              gap: {
                xs: 1.5,
                xl: 2,
              },
              alignItems: 'start',
            }}
          >
            <Stack spacing={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#e2e8f0',
                  fontWeight: 850,
                  mb: 0.15,
                }}
              >
                Profundidad del análisis
              </Typography>

              {comparisonAvailable
                && findingCopies[0] && (
                <MetricBlock
                  label="Principal componente del cambio"
                  value={
                    findingCopies[0].title
                  }
                  valueColor={movementColor(
                    findingCopies[0]
                      .toneValue,
                  )}
                  detail={
                    findingCopies[0].body
                  }
                />
              )}

              <MetricBlock
                label="Cobertura de costes"
                value={formatPercentage(
                  data.financial.current
                    .cost_coverage_revenue_pct,
                )}
                detail={
                  `${formatPercentage(
                    data.financial.current
                      .cost_coverage_rows_pct,
                  )} de operaciones con coste conocido`
                }
              />

              <MetricBlock
                label="Productos con desglose"
                value={
                  coverage
                    ? `${formatNumber(
                        coverage
                          .decomposition_eligible_relationships,
                        0,
                      )} / ${formatNumber(
                        coverage.relationships_total,
                        0,
                      )}`
                    : '—'
                }
                detail={
                  coverage
                    ? `${formatPercentage(
                        coverage
                          .eligible_share_of_absolute_documented_movement_pct,
                      )} de los cambios de margen cubiertos`
                    : undefined
                }
              />
            </Stack>

            <Stack spacing={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#e2e8f0',
                  fontWeight: 850,
                  mb: 0.15,
                }}
              >
                Situaciones detectadas
              </Typography>

              {!comparisonAvailable && (
                <Alert severity="info">
                  Selecciona una comparación temporal para detectar situaciones comerciales que merecen revisión.
                </Alert>
              )}

              {comparisonAvailable
                && investigationPatterns.length === 0 && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.4,
                    borderRadius: 2.5,
                    border:
                      '1px solid rgba(148,163,184,0.14)',
                    bgcolor:
                      'rgba(15,23,42,0.30)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    No se han detectado patrones prioritarios con la evidencia disponible para esta comparación.
                  </Typography>
                </Box>
              )}

              {comparisonAvailable
                && investigationPatterns.map(
                  (pattern) => {
                    const presentation =
                      investigationPatternCopy(
                        pattern,
                        effectiveCurrencyCode,
                      );

                    const isOpen =
                      openPatternCode
                      === pattern.code;

                    return (
                      <Box
                        key={pattern.code}
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          borderRadius: 2.5,
                          border:
                            '1px solid rgba(148,163,184,0.14)',
                          bgcolor:
                            'rgba(15,23,42,0.30)',
                        }}
                      >
                        <Stack spacing={0.65}>
                          <Stack
                            direction={{
                              xs: 'column',
                              md: 'row',
                            }}
                            spacing={1}
                            alignItems={{
                              xs: 'flex-start',
                              md: 'center',
                            }}
                            justifyContent="space-between"
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                                alignItems="center"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color:
                                      movementColor(
                                        presentation
                                          .toneValue,
                                      ),
                                    fontWeight: 850,
                                  }}
                                >
                                  {presentation.title}
                                </Typography>

                                <Chip
                                  size="small"
                                  label={
                                    presentation.tag
                                  }
                                  sx={{
                                    height: 22,
                                    bgcolor:
                                      'rgba(56,189,248,0.08)',
                                    color: '#bae6fd',
                                    border:
                                      '1px solid rgba(56,189,248,0.14)',
                                    fontWeight: 750,
                                    fontSize:
                                      '0.68rem',
                                  }}
                                />
                              </Stack>

                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.35,
                                  color: '#94a3b8',
                                  lineHeight: 1.45,
                                }}
                              >
                                {presentation.body}
                              </Typography>

                              {pattern.metrics
                                .share_of_absolute_documented_movement_pct
                                !== null && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mt: 0.35,
                                    color: '#64748b',
                                  }}
                                >
                                  {formatPercentage(
                                    pattern.metrics
                                      .share_of_absolute_documented_movement_pct,
                                  )} del movimiento absoluto de margen.
                                </Typography>
                              )}
                            </Box>

                            {pattern.top_relationships
                              .length > 0 && (
                              <Button
                                size="small"
                                variant="text"
                                endIcon={
                                  isOpen
                                    ? <ExpandLessRoundedIcon />
                                    : <ExpandMoreRoundedIcon />
                                }
                                onClick={() =>
                                  setOpenPatternCode(
                                    isOpen
                                      ? null
                                      : pattern.code,
                                  )
                                }
                                sx={{
                                  flexShrink: 0,
                                }}
                              >
                                Ver evidencia (
                                {
                                  pattern
                                    .top_relationships
                                    .length
                                }
                                )
                              </Button>
                            )}
                          </Stack>

                          <Collapse in={isOpen}>
                            <Stack
                              spacing={0.5}
                              sx={{
                                mt: 0.75,
                                pt: 0.75,
                                borderTop:
                                  '1px solid rgba(148,163,184,0.10)',
                              }}
                            >
                              {pattern.top_relationships.map(
                                (relationship) => (
                                  <Box
                                    key={
                                      relationship
                                        .product_id
                                    }
                                    sx={{
                                      display:
                                        'grid',
                                      gridTemplateColumns:
                                        {
                                          xs: '1fr',
                                          sm:
                                            'minmax(0, 1fr) auto auto',
                                        },
                                      gap: 1,
                                      alignItems:
                                        'center',
                                      px: 1,
                                      py: 0.7,
                                      borderRadius: 2,
                                      bgcolor:
                                        'rgba(2,6,23,0.20)',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color:
                                          '#e2e8f0',
                                        fontWeight:
                                          750,
                                      }}
                                    >
                                      {
                                        relationship
                                          .label
                                      }
                                    </Typography>

                                    <Stack
                                      spacing={0}
                                      sx={{
                                        textAlign:
                                          'right',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color:
                                            movementColor(
                                              relationship
                                                .margin_delta_documented,
                                            ),
                                          fontWeight:
                                            800,
                                        }}
                                      >
                                        Margen{' '}
                                        {formatSignedMoney(
                                          relationship
                                            .margin_delta_documented,
                                          effectiveCurrencyCode,
                                        )}
                                      </Typography>

                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color:
                                            '#64748b',
                                        }}
                                      >
                                        Ingresos{' '}
                                        {formatSignedMoney(
                                          relationship
                                            .revenue_delta,
                                          effectiveCurrencyCode,
                                        )}
                                      </Typography>
                                    </Stack>

                                    <Button
                                      size="small"
                                      variant="text"
                                      endIcon={
                                        <OpenInNewRoundedIcon />
                                      }
                                      onClick={() =>
                                        openRelationship(
                                          relationship
                                            .product_id,
                                        )
                                      }
                                    >
                                      Examinar
                                    </Button>
                                  </Box>
                                ),
                              )}
                            </Stack>
                          </Collapse>
                        </Stack>
                      </Box>
                    );
                  },
                )}
            </Stack>
          </Box>

          {data.limitations.length > 0 && (
            <Alert severity="info">
              {data.limitations
                .map(limitationText)
                .join(' ')}
            </Alert>
          )}

          {comparisonAvailable && contributions && (
            <>
              <Divider />

              <Box>
                <Button
                  variant="outlined"
                  startIcon={
                    <AccountTreeRoundedIcon />
                  }
                  endIcon={
                    detailsOpen
                      ? <ExpandLessRoundedIcon />
                      : <ExpandMoreRoundedIcon />
                  }
                  onClick={() =>
                    setDetailsOpen(
                      (current) => !current,
                    )
                  }
                >
                  Ver qué productos explican el cambio
                </Button>

                <Collapse in={detailsOpen}>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        xl:
                          'minmax(300px, 0.8fr) repeat(2, minmax(0, 1fr))',
                      },
                      gap: 1.5,
                      alignItems: 'start',
                    }}
                  >
                    {decomposition
                      && decomposition
                        .eligible_relationship_count > 0 && (
                      <Box
                        sx={{
                          minWidth: 0,
                          gridColumn: {
                            xs: '1',
                            xl: '1',
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: '#e2e8f0',
                            fontWeight: 850,
                            mb: 1,
                          }}
                        >
                          Qué ha movido el margen en los productos comparables
                        </Typography>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'repeat(3, minmax(0, 1fr))',
                              xl: '1fr',
                            },
                            gap: 1,
                          }}
                        >
                          <MetricBlock
                            label="Por cantidad"
                            value={formatSignedMoney(
                              decomposition.quantity_effect,
                              effectiveCurrencyCode,
                            )}
                            valueColor={movementColor(
                              decomposition.quantity_effect,
                            )}
                          />

                          <MetricBlock
                            label="Por precio"
                            value={formatSignedMoney(
                              decomposition.price_effect,
                              effectiveCurrencyCode,
                            )}
                            valueColor={movementColor(
                              decomposition.price_effect,
                            )}
                          />

                          <MetricBlock
                            label="Por coste"
                            value={formatSignedMoney(
                              decomposition.cost_effect,
                              effectiveCurrencyCode,
                            )}
                            valueColor={movementColor(
                              decomposition.cost_effect,
                            )}
                          />
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 1,
                            color: '#64748b',
                            lineHeight: 1.5,
                          }}
                        >
                          Este reparto muestra cuánto cambia el margen por cantidad, precio y coste. No indica por qué el cliente compró más o menos.
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        minWidth: 0,
                        gridColumn: {
                          xs: '1',
                          xl: '2 / span 2',
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: '#e2e8f0',
                          fontWeight: 850,
                          mb: 1,
                        }}
                      >
                        Productos con mayor cambio de margen
                      </Typography>

                      {topRelationships.length === 0 ? (
                        <Typography
                          variant="body2"
                          sx={{ color: '#94a3b8' }}
                        >
                          No existen relaciones con variación documentada para esta comparación.
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              xl:
                                'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 0.75,
                          }}
                        >
                          {topRelationships.map(
                            (relationship) => (
                              <Box
                                key={relationship.product_id}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: {
                                    xs: '1fr',
                                    md:
                                      'minmax(0, 1fr) auto auto',
                                  },
                                  gap: 1,
                                  alignItems: 'center',
                                  px: 1.25,
                                  py: 1,
                                  borderRadius: 2,
                                  border:
                                    '1px solid rgba(148,163,184,0.12)',
                                  bgcolor:
                                    'rgba(15,23,42,0.30)',
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#f8fafc',
                                      fontWeight: 750,
                                    }}
                                  >
                                    {relationship.label}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                    }}
                                  >
                                    {relationship.observation_class === 'continued'
                                      ? 'Comprado en ambos periodos'
                                      : relationship.observation_class === 'current_only'
                                        ? 'Actividad solo actual'
                                        : relationship.observation_class === 'comparison_only'
                                          ? 'Actividad solo comparada'
                                          : 'Sin comparación'}
                                    {relationship.decomposition_eligible
                                      ? ' · desglose disponible'
                                      : ''}
                                  </Typography>
                                </Box>

                                <Stack
                                  direction="row"
                                  spacing={1.5}
                                  sx={{
                                    minWidth: {
                                      md: 220,
                                    },
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#64748b',
                                      }}
                                    >
                                      Δ margen
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color:
                                          movementColor(
                                            relationship
                                              .margin_delta_documented,
                                          ),
                                        fontWeight: 850,
                                      }}
                                    >
                                      {formatSignedMoney(
                                        relationship
                                          .margin_delta_documented,
                                        effectiveCurrencyCode,
                                      )}
                                    </Typography>
                                  </Box>

                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#64748b',
                                      }}
                                    >
                                      Peso
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#e2e8f0',
                                        fontWeight: 800,
                                      }}
                                    >
                                      {formatPercentage(
                                        relationship
                                          .share_of_absolute_documented_movement_pct,
                                      )}
                                    </Typography>
                                  </Box>
                                </Stack>

                                <Button
                                  size="small"
                                  variant="text"
                                  endIcon={
                                    <OpenInNewRoundedIcon />
                                  }
                                  onClick={() =>
                                    openRelationship(
                                      relationship.product_id,
                                    )
                                  }
                                >
                                  Examinar
                                </Button>
                              </Box>
                            ),
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
